import { useAppDispatch } from "@app/hooks";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldInput } from "@emerald-react/input";
import {
  useDeletePdfSectionMutation,
  useUpdatePdfSectionMutation,
} from "@services/survey-sections";
import { CustomSectionPDF } from "@survey-types/survey.section.types";
import { deleteSectionPdf, updateSectionPdf } from "@survey/surveySlice";
import { KeyboardEvent, useState } from "react";

interface FileListProps {
  file: CustomSectionPDF;
  surveyId: string;
  sectionId: string;
}

const FileList = ({ file, surveyId, sectionId }: FileListProps) => {
  const { mediaName, mediaSize, surveySectionMediaId } = file;
  const dispatch = useAppDispatch();
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [editFileName, setEditFileName] = useState("");
  const [updatePdfSection] = useUpdatePdfSectionMutation();
  const [deletePdfSection] = useDeletePdfSectionMutation();

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleEditFile = (pdf: CustomSectionPDF) => {
    setEditingFileId(pdf.surveySectionMediaId);
    setEditFileName(pdf.mediaName);
  };

  const handleSaveFileName = async (pdfId: string) => {
    // Update Redux store first for immediate UI feedback
    dispatch(
      updateSectionPdf({
        sectionId,
        pdfId,
        updates: { mediaName: editFileName.trim() },
      }),
    );

    try {
      await updatePdfSection({
        surveySectionMediaId: pdfId,
        mediaName: editFileName.trim(),
        surveyId: surveyId,
      }).unwrap();

      setEditingFileId(null);
      setEditFileName("");
    } catch (_error) {
      console.error("Error updating file name");
    }
  };

  const handleCancelEdit = () => {
    setEditingFileId(null);
    setEditFileName("");
  };

  const handleDeleteFile = async (pdfId: string) => {
    // Update Redux store first for immediate UI feedback
    dispatch(deleteSectionPdf({ sectionId, pdfId }));

    try {
      await deletePdfSection({
        surveySectionMediaId: pdfId,
        surveyId: surveyId,
      }).unwrap();
    } catch (_error) {
      console.error("Error deleting file");
    }
  };

  const handleKeyDown = (e: KeyboardEvent, pdfId: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveFileName(pdfId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  return (
    <div
      key={surveySectionMediaId}
      className="flex items-center justify-between py-2 px-1"
    >
      <div className="flex-1 min-w-0">
        {editingFileId === surveySectionMediaId ? (
          <EmeraldInput
            value={editFileName}
            onChange={(e) => setEditFileName(e.target.value)}
            onBlur={() => handleSaveFileName(surveySectionMediaId)}
            onKeyDown={(e) => handleKeyDown(e, surveySectionMediaId)}
            className="text-sm"
            autoFocus
          />
        ) : (
          <div>
            <p
              className="text-sm font-medium text-gray-900 cursor-pointer hover:text-gray-600 wrap-break-word"
              onClick={() => handleEditFile(file)}
              title={mediaName}
            >
              {mediaName}
            </p>
            <p className="text-sm text-gray-500">
              PDF {formatFileSize(mediaSize)}
            </p>
          </div>
        )}
      </div>
      <div className="flex items-center">
        {editingFileId !== surveySectionMediaId && (
          <>
            <EmeraldIconButton
              icon="edit"
              type="Icon"
              onClick={() => handleEditFile(file)}
              title="Edit"
            />
            <EmeraldIconButton
              icon="delete"
              type="Icon"
              onClick={() => handleDeleteFile(surveySectionMediaId)}
              title="Delete"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default FileList;
